import { useState } from "react";
import PageHero from "@/components/PageHero";
import CTA from "@/components/CTA";
import { submitMailto } from "@/lib/mailto";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";

const SIZES = ["1–5", "6–20", "21–50", "51–200", "200+"];
const COUNTRIES = ["United States", "Canada", "United Kingdom", "Australia", "India", "Germany", "France", "Other"];

export default function RequestDemo() {
  const [f, setF] = useState({
    name: "",
    clinic: "",
    lab: "",
    email: "",
    phone: "",
    country: "United States",
    size: "6–20",
    message: "",
    consent: true,
  });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    const body = [
      `Name: ${f.name}`,
      `Clinic: ${f.clinic}`,
      `Lab: ${f.lab}`,
      `Email: ${f.email}`,
      `Phone: ${f.phone}`,
      `Country: ${f.country}`,
      `Company size: ${f.size}`,
      "",
      "Message:",
      f.message,
    ].join("\n");
    submitMailto({
      to: "sales@rxn3d.com",
      subject: `[Rxn3D · Demo] ${f.name || "New demo request"}`,
      body,
      testLabel: "demo",
    });
  };

  return (
    <>
      <PageHero
        chapter="§ 11"
        eyebrow="Request demo"
        title={"See Rxn3D / in 20 minutes."}
        description="Tell us a little about your lab or clinic. We'll tailor the walkthrough to your operation."
      />

      <section data-testid="demo-grid" className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
          <motion.form
            onSubmit={onSubmit}
            data-testid="demo-form"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-8 rounded-3xl hairline glass p-8 lg:p-10"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full name" required value={f.name} onChange={set("name")} name="name" />
              <Field label="Work email" required type="email" value={f.email} onChange={set("email")} name="email" />
              <Field label="Clinic" value={f.clinic} onChange={set("clinic")} name="clinic" />
              <Field label="Lab name" value={f.lab} onChange={set("lab")} name="lab" />
              <Field label="Phone" value={f.phone} onChange={set("phone")} name="phone" />
              <SelectField label="Country" value={f.country} onChange={set("country")} name="country" options={COUNTRIES} />
              <SelectField label="Company size" value={f.size} onChange={set("size")} name="size" options={SIZES} className="sm:col-span-2" />
              <Field label="What would you like to see?" name="message" value={f.message} onChange={set("message")} as="textarea" className="sm:col-span-2" />
            </div>

            <label className="mt-6 flex items-start gap-3 text-sm text-muted-foreground">
              <input
                type="checkbox"
                data-testid="demo-consent"
                checked={f.consent}
                onChange={set("consent")}
                className="mt-1 h-4 w-4 accent-[#82298D]"
              />
              I agree to be contacted about Rxn3D and understand my data is handled per the privacy policy.
            </label>

            <button
              type="submit"
              data-testid="demo-submit"
              disabled={!f.consent}
              className="mt-8 group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-foreground text-background text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-40"
            >
              Request demo
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </motion.form>

          <motion.aside
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-4 space-y-3"
          >
            <div className="rounded-3xl bg-brand-ink text-white p-8 relative overflow-hidden grain">
              <div className="absolute -top-24 -right-16 h-48 w-48 rounded-full bg-brand-gradient blur-3xl opacity-70" />
              <div className="relative">
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/60 mb-4">What to expect</div>
                <ul className="space-y-3 text-sm">
                  {[
                    "20-minute personalized walkthrough",
                    "Live product, tailored to your operation",
                    "Q&A with a real product specialist",
                    "Migration & onboarding overview",
                  ].map((v) => (
                    <li key={v} className="flex items-start gap-3">
                      <Check className="h-4 w-4 mt-0.5" />
                      {v}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* <div className="rounded-3xl hairline glass p-6">
              <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">Prefer email?</div>
              <div className="font-heading text-lg font-semibold mt-1">sales@rxn3d.com</div>
              <div className="text-xs text-muted-foreground mt-2">
                We reply within one business day.
              </div>
            </div> */}
          </motion.aside>
        </div>
      </section>

      <CTA />
    </>
  );
}

function Field({ label, name, value, onChange, type = "text", as = "input", required, className = "" }) {
  return (
    <label className={`block ${className}`} htmlFor={name}>
      <span className="text-xs font-mono tracking-[0.2em] uppercase text-muted-foreground">{label}{required && " *"}</span>
      {as === "textarea" ? (
        <textarea
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          rows={5}
          data-testid={`demo-field-${name}`}
          className="mt-2 w-full px-4 py-3.5 rounded-2xl hairline glass bg-transparent text-sm outline-none focus:ring-2 focus:ring-brand-plum/40"
        />
      ) : (
        <input
          type={type}
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          data-testid={`demo-field-${name}`}
          className="mt-2 w-full px-4 py-3.5 rounded-2xl hairline glass bg-transparent text-sm outline-none focus:ring-2 focus:ring-brand-plum/40"
        />
      )}
    </label>
  );
}

function SelectField({ label, name, value, onChange, options, className = "" }) {
  return (
    <label className={`block ${className}`} htmlFor={name}>
      <span className="text-xs font-mono tracking-[0.2em] uppercase text-muted-foreground">{label}</span>
      <select
        id={name}
        name={name}
        value={value}
        onChange={onChange}
        data-testid={`demo-field-${name}`}
        className="mt-2 w-full px-4 py-3.5 rounded-2xl hairline glass bg-transparent text-sm outline-none focus:ring-2 focus:ring-brand-plum/40 appearance-none"
      >
        {options.map((o) => (
          <option key={o} value={o} className="bg-background">
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}
